import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, PackageX, Pencil, Check, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { money, assetUrl } from "@/lib/api";
import { ConditionBadge } from "@/components/ConditionBadge";
import { Stars } from "@/components/ProductReviews";
import { HeartButton } from "@/components/HeartButton";
import { toast } from "sonner";

export const GRADE_ORDER = [
  "New / Brand New / In Box (NIB)",
  "Like New / Mint Condition",
  "New Other / Scratch and Dent",
  "Floor Model / Display",
  "Good / Fair Condition / Tested & Working",
  "Used / Pre-owned / Tested & Working",
  "As-Is / Untested / Salvage / For Parts",
];

const stockOf = (p) => p.available ?? p.quantity ?? 0;

function Media({ img, video, name }) {
  if (img) {
    return <div className="aspect-square overflow-hidden bg-surface"><img src={assetUrl(img)} alt={name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-200" /></div>;
  }
  if (video) {
    return <div className="aspect-square overflow-hidden bg-ink"><video src={assetUrl(video)} className="h-full w-full object-cover" autoPlay loop muted playsInline /></div>;
  }
  return <div className="aspect-square flex items-center justify-center text-subtle bg-surface"><PackageX className="h-8 w-8" /></div>;
}

function AdminPrice({ product, price, big, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(price ?? ""));
  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
  const save = async (e) => {
    stop(e);
    const num = Number(val);
    if (isNaN(num) || num < 0) { toast.error("Enter a valid price"); return; }
    try {
      const { data } = await api.post(`/products/${product.id}/price`, { our_price: num });
      toast.success("Price updated");
      setEditing(false);
      onSaved(data.our_price);
    } catch { toast.error("Update failed"); }
  };
  if (editing) {
    return (
      <span className="inline-flex items-center gap-1" onClick={stop}>
        <input autoFocus type="number" step="0.01" value={val} onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") save(e); if (e.key === "Escape") { stop(e); setEditing(false); } }}
          data-testid={`price-input-${product.id}`}
          className="w-20 border border-ink h-7 px-1 text-sm font-mono" />
        <button data-testid={`price-save-${product.id}`} onClick={save} className="h-6 w-6 flex items-center justify-center bg-ink text-paper"><Check className="h-3 w-3" /></button>
        <button onClick={(e) => { stop(e); setEditing(false); }} className="h-6 w-6 flex items-center justify-center border border-ink"><X className="h-3 w-3" /></button>
      </span>
    );
  }
  return (
    <button data-testid={`price-edit-${product.id}`} onClick={(e) => { stop(e); setEditing(true); }}
      className={`inline-flex items-center gap-1 font-heading font-black tracking-tighter leading-none hover:text-link ${big ? "text-2xl" : "text-lg"}`}>
      {money(price)}<Pencil className="h-3 w-3 opacity-50" />
    </button>
  );
}

export function ProductCard({ product, variants }) {
  const { add } = useCart();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [prices, setPrices] = useState({});
  const [activeId, setActiveId] = useState(null);
  const priceOf = (g) => (g.id in prices ? prices[g.id] : g.our_price);

  const gradeRank = (c) => { const i = GRADE_ORDER.indexOf(c); return i === -1 ? 999 : i; };
  const grades = (variants && variants.length ? variants : [product])
    .slice()
    .sort((a, b) => (gradeRank(a.condition) - gradeRank(b.condition)) || (priceOf(b) || 0) - (priceOf(a) || 0));
  const primary = product || grades[0];
  const multi = grades.length > 1;
  const allSoldOut = grades.every((g) => stockOf(g) <= 0);

  const heroGrade = grades.find((g) => g.images && g.images[0]) || grades[0];
  const activeGrade = grades.find((g) => g.id === activeId) || heroGrade;
  const img = (activeGrade?.images || [])[0] || (grades.find((g) => g.images && g.images[0])?.images || [])[0] || "";
  const video = (activeGrade?.videos || [])[0] || "";
  const maxDiscount = Math.max(0, ...grades.map((g) => g.discount_pct || 0));
  const markedDown = grades.some((g) => g.recently_discounted);

  const addGrade = (g) => { add({ ...g, our_price: priceOf(g) }, 1); toast.success(`${g.name}${multi ? ` (${g.condition})` : ""} added to cart`); };
  const setPrice = (id, v) => setPrices((p) => ({ ...p, [id]: v }));

  return (
    <div data-testid={`product-card-${primary.id}`} className="group bg-paper border border-ink flex flex-col hover:shadow-hard transition-all duration-150">
      <Link to={`/product/${primary.id}`} className="relative block overflow-hidden border-b border-ink">
        <Media img={img} video={video} name={primary.name} />
        {maxDiscount > 0 && (
          <div className="absolute top-0 left-0 bg-discount text-ink font-heading font-black text-lg px-2 py-1 border-r border-b border-ink">-{maxDiscount}%</div>
        )}
        {markedDown && (
          <div data-testid={`markdown-badge-${primary.id}`} className="absolute top-9 left-0 bg-danger text-white font-mono text-[9px] uppercase tracking-wider px-2 py-0.5 border-r border-b border-ink">Marked Down</div>
        )}
        <HeartButton product={primary} className="absolute top-2 right-2 h-8 w-8 bg-paper/90 border border-ink hover:bg-danger hover:text-white" />
        {multi
          ? <div className="absolute bottom-2 right-2"><ConditionBadge condition={activeGrade?.condition || primary.condition} /></div>
          : <div className="absolute bottom-2 right-2"><ConditionBadge condition={primary.condition} /></div>}
        {allSoldOut && (
          <div className="absolute inset-0 bg-ink/70 flex items-center justify-center">
            <span className="bg-danger text-white font-mono text-xs uppercase tracking-widest px-3 py-1 border border-white">Sold Out</span>
          </div>
        )}
      </Link>

      <div className="p-3 flex flex-col flex-1">
        <div className="font-mono text-[10px] text-subtle uppercase tracking-wider">{primary.sku || "—"} · {primary.category}</div>
        <Link to={`/product/${primary.id}`} className="font-heading font-bold text-sm leading-tight mt-1 line-clamp-2 hover:text-link transition-colors">{primary.name}</Link>
        {primary.rating_count > 0 && (
          <div className="flex items-center gap-1.5 mt-1" data-testid={`card-rating-${primary.id}`}>
            <Stars value={primary.rating_avg} size="h-3 w-3" />
            <span className="font-mono text-[10px] text-subtle">{primary.rating_avg} ({primary.rating_count})</span>
          </div>
        )}

        {multi ? (
          <div className="mt-3 pt-1 border-t border-ink/15 divide-y divide-ink/15" data-testid={`grade-list-${primary.id}`}>
            <div className="font-mono text-[9px] uppercase tracking-widest text-subtle pb-1">{grades.length} grades · tap a condition to see its photos</div>
            {grades.map((g) => {
              const s = stockOf(g);
              const out = s <= 0;
              const isActive = activeGrade?.id === g.id;
              return (
                <div key={g.id} role="button" tabIndex={0} data-testid={`grade-select-${g.id}`}
                  onClick={() => setActiveId(g.id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setActiveId(g.id); } }}
                  title="Show this unit's photos"
                  className={`flex items-center justify-between gap-2 py-2 -mx-1 px-1 cursor-pointer transition-colors ${isActive ? "bg-surface" : "hover:bg-surface/60"}`}>
                  <div className="min-w-0 text-left">
                    <ConditionBadge condition={g.condition} />
                    <div className="mt-1 font-mono text-[10px] uppercase tracking-wider flex items-center gap-1" style={{ color: !out && s <= 3 ? "#FF3B30" : "#71717A" }}>
                      {out ? "Out of stock" : `${s} available`}
                      {isActive && <span className="text-link normal-case">· showing</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    {g.list_price > 0 && <div className="font-mono text-[10px] text-subtle line-through">{money(g.list_price)}</div>}
                    {isAdmin
                      ? <AdminPrice product={g} price={priceOf(g)} onSaved={(v) => setPrice(g.id, v)} />
                      : <div className="font-heading font-black text-lg tracking-tighter leading-none">{money(priceOf(g))}</div>}
                  </div>
                  <button data-testid={`add-to-cart-${g.id}`} disabled={out} onClick={(e) => { e.stopPropagation(); addGrade(g); }}
                    className="h-8 w-8 shrink-0 flex items-center justify-center bg-ink text-paper hover:bg-link transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <>
            <div className="mt-auto pt-3 flex items-end justify-between">
              <div>
                {primary.list_price > 0 && <div className="font-mono text-xs text-subtle line-through">{money(primary.list_price)}</div>}
                {isAdmin
                  ? <AdminPrice product={primary} price={priceOf(primary)} big onSaved={(v) => setPrice(primary.id, v)} />
                  : <div className="font-heading font-black text-2xl tracking-tighter leading-none">{money(priceOf(primary))}</div>}
              </div>
              <button data-testid={`add-to-cart-${primary.id}`} disabled={allSoldOut} onClick={() => addGrade(primary)}
                className="h-9 w-9 flex items-center justify-center bg-ink text-paper hover:bg-link transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-2 font-mono text-[10px] uppercase tracking-wider" style={{ color: stockOf(primary) <= 3 && !allSoldOut ? "#FF3B30" : "#71717A" }} data-testid={`stock-note-${primary.id}`}>
              {allSoldOut ? "Out of stock" : `${stockOf(primary)} available · `}<span className="normal-case">{allSoldOut ? "" : primary.condition}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
